import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/repricing";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};

const percent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : "";
};

const numberText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const priorityColor = (priority) => {
  if (priority === "high") return "error";
  if (priority === "medium") return "warning";
  if (priority === "blocked") return "default";
  return "primary";
};

const stageLabel = (stage) =>
  ({
    no_listings: "No listings",
    needs_revenue_data: "Needs revenue data",
    sold_through: "Sold through",
    capital_recovered_tail: "Capital recovered tail",
    capital_recovered: "Capital recovered",
    stale_remaining_review: "Stale remaining review",
    recovering: "Recovering",
    listed: "Listed",
  }[stage] || stage || "Review");

export default function RepricingListingCandidates() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/listing-candidates?limit=250`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load listing candidates");
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSummary(data.summary || null);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setWarnings([]);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const columns = useMemo(
    () => [
      {
        field: "sku",
        headerName: "SKU",
        width: 120,
      },
      {
        field: "listing_title",
        headerName: "Listing",
        flex: 1,
        minWidth: 210,
      },
      {
        field: "recommendation_key",
        headerName: "Recommendation",
        minWidth: 185,
        renderCell: (params) => (
          <Chip
            size="small"
            color={priorityColor(params.row.priority)}
            label={params.value || "monitor"}
          />
        ),
      },
      {
        field: "blocked_reason",
        headerName: "Blocked reason",
        minWidth: 220,
        flex: 1,
        renderCell: (params) => {
          const reasons = Array.isArray(params.row.blocked_reasons) ? params.row.blocked_reasons : [];
          if (!reasons.length) return <Typography variant="body2">-</Typography>;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {reasons.slice(0, 2).map((reason) => (
                <Chip key={reason} size="small" variant="outlined" label={reason} />
              ))}
              {reasons.length > 2 && <Chip size="small" label={`+${reasons.length - 2}`} />}
            </Stack>
          );
        },
      },
      {
        field: "app_price",
        headerName: "App price",
        width: 110,
        renderCell: (params) => money(params.value),
      },
      {
        field: "ebay_current_price",
        headerName: "eBay price",
        width: 115,
        renderCell: (params) => money(params.value),
      },
      {
        field: "unit_cost",
        headerName: "Cost",
        width: 100,
        renderCell: (params) => money(params.value),
      },
      {
        field: "expected_profit",
        headerName: "Profit",
        width: 105,
        renderCell: (params) => money(params.value),
      },
      {
        field: "expected_margin",
        headerName: "Margin",
        width: 105,
        renderCell: (params) => percent(params.value),
      },
      {
        field: "po_stage",
        headerName: "PO stage",
        minWidth: 170,
        renderCell: (params) => <Chip size="small" variant="outlined" label={stageLabel(params.value)} />,
      },
      {
        field: "views_30d",
        headerName: "Views 30d",
        width: 105,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "quantity_sold_30d",
        headerName: "Sold 30d",
        width: 100,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "traffic_sales_amount_30d",
        headerName: "Sales 30d",
        width: 115,
        renderCell: (params) => money(params.value),
      },
      {
        field: "ad_spend_30d",
        headerName: "Ad spend 30d",
        width: 130,
        renderCell: (params) => money(params.value),
      },
      {
        field: "last_ebay_sync_at",
        headerName: "Last eBay sync",
        width: 165,
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Listing Candidates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deterministic v1 preview rules only. No candidate rows are saved and no price push workflow is enabled.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadRows} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${numberText(summary.candidate_count)} candidates`} />
          <Chip label={`${numberText(summary.review_count)} review signals`} />
          <Chip label={`${numberText(summary.blocked_count)} blocked`} />
          <Chip color="primary" label="Preview only" />
        </Stack>
      )}

      <Paper variant="outlined" sx={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "recommendation_key", sort: "asc" }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
